import { useState } from 'react';

export default function CTA() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('message', formData.message || 'Solicitud de compra del ebook');

      const response = await fetch('https://readdy.ai/api/form/d5holjrgekujqjrtsp30', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="cta" className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 bg-gradient-to-b from-amber-50 to-orange-50">
      <div className="max-w-7xl mx-auto">
        {/* Hero CTA with Background */}
        <div className="relative rounded-2xl sm:rounded-3xl overflow-hidden mb-8 sm:mb-12 md:mb-16 shadow-2xl">
          <div className="absolute inset-0">
            <img
              src="https://public.readdy.ai/ai/img_res/ae9c108ef8d09c3ce1ae65791e4d948b.jpg"
              alt="Familia conectando"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-gray-900/80 via-gray-800/70 to-gray-900/80"></div>
          </div>

          <div className="relative z-10 py-12 sm:py-16 md:py-24 px-4 sm:px-8 md:px-16">
            <div className="max-w-4xl">
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold text-white mb-4 sm:mb-6 md:mb-8 leading-tight">
                🌿 Regálale a tu hijo<br />
                algo que dura<br />
                toda la vida
              </h2>
              <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-white mb-4 sm:mb-6 md:mb-8 max-w-2xl leading-relaxed">
                Más que un libro, "El poder de ser yo" es una invitación a criar desde el amor, la calma y la conexión emocional.
              </p>
              <p className="text-xl sm:text-2xl md:text-3xl text-white font-bold mb-6 sm:mb-8 md:mb-12">
                ✨ Empieza hoy este viaje en familia por solo <span className="text-orange-400">$9 dólares</span> ✨
              </p>
            </div>
          </div>
        </div>

        {/* Bonuses Section */}
        <div className="max-w-4xl mx-auto mb-8 sm:mb-12 md:mb-16">
          <div className="bg-gradient-to-br from-orange-500 to-rose-500 rounded-2xl sm:rounded-3xl shadow-2xl p-6 sm:p-8 md:p-12 text-white">
            <div className="text-center mb-6 sm:mb-8">
              <div className="inline-block bg-white/20 backdrop-blur-sm px-4 sm:px-6 py-2 rounded-full mb-3 sm:mb-4">
                <span className="text-white font-bold text-sm sm:text-base">🎁 BONOS INCLUIDOS</span>
              </div>
              <h3 className="font-serif text-2xl sm:text-3xl md:text-4xl font-bold mb-2 sm:mb-3">
                Acceso completo + Bonos especiales
              </h3>
              <p className="text-base sm:text-lg text-white/90">
                Todo lo que necesitas para empezar hoy mismo
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-6 border-2 border-white/30 hover:bg-white/20 transition-all hover:scale-105">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-white/20 rounded-full flex items-center justify-center mb-3 sm:mb-4">
                  <i className="ri-download-cloud-line text-2xl sm:text-3xl text-white"></i>
                </div>
                <h4 className="font-bold text-lg sm:text-xl mb-2">Descarga inmediata</h4>
                <p className="text-sm sm:text-base text-white/90">Acceso instantáneo al ebook completo en formato digital</p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-6 border-2 border-white/30 hover:bg-white/20 transition-all hover:scale-105">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-white/20 rounded-full flex items-center justify-center mb-3 sm:mb-4">
                  <i className="ri-device-line text-2xl sm:text-3xl text-white"></i>
                </div>
                <h4 className="font-bold text-lg sm:text-xl mb-2">Multi-dispositivo</h4>
                <p className="text-sm sm:text-base text-white/90">Lee en tablet, móvil, computadora o imprime cuando quieras</p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-6 border-2 border-white/30 hover:bg-white/20 transition-all hover:scale-105">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-white/20 rounded-full flex items-center justify-center mb-3 sm:mb-4">
                  <i className="ri-refresh-line text-2xl sm:text-3xl text-white"></i>
                </div>
                <h4 className="font-bold text-lg sm:text-xl mb-2">Actualizaciones gratis</h4>
                <p className="text-sm sm:text-base text-white/90">Recibe todas las actualizaciones futuras sin costo adicional</p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-6 border-2 border-white/30 hover:bg-white/20 transition-all hover:scale-105">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-white/20 rounded-full flex items-center justify-center mb-3 sm:mb-4">
                  <i className="ri-heart-line text-2xl sm:text-3xl text-white"></i>
                </div>
                <h4 className="font-bold text-lg sm:text-xl mb-2">Soporte familiar</h4>
                <p className="text-sm sm:text-base text-white/90">Guía y recursos adicionales para toda la familia</p>
              </div>
            </div>

            <div className="mt-6 sm:mt-8 text-center">
              <p className="text-base sm:text-lg font-bold mb-2">Valor total: <span className="line-through text-white/70">$47</span></p>
              <p className="text-2xl sm:text-3xl md:text-4xl font-bold">Hoy solo: $9 USD 🎉</p>
            </div>
          </div>
        </div>

        {/* Purchase Form */}
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl p-6 sm:p-8 md:p-12 border-4 border-orange-500">
            <div className="text-center mb-6 sm:mb-8">
              <div className="inline-block bg-gradient-to-r from-orange-500 to-rose-500 text-white px-4 sm:px-6 py-2 rounded-full mb-3 sm:mb-4 font-bold text-sm sm:text-base animate-pulse">
                ⚡ OFERTA ESPECIAL
              </div>
              <h3 className="font-serif text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
                Obtén tu ebook ahora
              </h3>
              <p className="text-gray-700 text-base sm:text-lg font-medium">
                Completa el formulario y recibe acceso inmediato
              </p>
            </div>

            <form id="purchase-form" data-readdy-form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm sm:text-base font-bold text-gray-900 mb-2">
                  Nombre completo *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 sm:py-4 border-2 border-gray-300 rounded-xl focus:border-orange-500 focus:outline-none transition-colors text-base sm:text-lg text-gray-900 font-medium"
                  placeholder="Tu nombre"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm sm:text-base font-bold text-gray-900 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 sm:py-4 border-2 border-gray-300 rounded-xl focus:border-orange-500 focus:outline-none transition-colors text-base sm:text-lg text-gray-900 font-medium"
                  placeholder="tu@email.com"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm sm:text-base font-bold text-gray-900 mb-2">
                  Mensaje (opcional)
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={4}
                  maxLength={500}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="w-full px-4 py-3 sm:py-4 border-2 border-gray-300 rounded-xl focus:border-orange-500 focus:outline-none transition-colors resize-none text-base sm:text-lg text-gray-900"
                  placeholder="¿Tienes alguna pregunta o comentario?"
                ></textarea>
                <p className="text-sm text-gray-600 mt-1 font-medium">{formData.message.length}/500 caracteres</p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-orange-500 to-rose-500 text-white py-4 sm:py-5 rounded-xl font-bold text-lg sm:text-xl hover:shadow-2xl transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer hover:scale-105 active:scale-95 shadow-xl"
              >
                {isSubmitting ? '⏳ Procesando...' : '🎁 Comprar por $9 USD'}
              </button>

              {submitStatus === 'success' && (
                <div className="bg-emerald-100 border-4 border-emerald-500 rounded-xl p-4 sm:p-6 text-center animate-pulse">
                  <div className="w-16 h-16 bg-emerald-500 rounded-full flex items-center justify-center mx-auto mb-3">
                    <i className="ri-check-line text-white text-4xl"></i>
                  </div>
                  <p className="text-emerald-900 font-bold text-lg sm:text-xl">
                    ¡Gracias! Recibirás un email con los detalles de acceso.
                  </p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-100 border-4 border-red-500 rounded-xl p-4 sm:p-6 text-center">
                  <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-3">
                    <i className="ri-error-warning-line text-white text-4xl"></i>
                  </div>
                  <p className="text-red-900 font-bold text-lg sm:text-xl">
                    Hubo un error. Por favor, intenta nuevamente.
                  </p>
                </div>
              )}
            </form>

            {/* Trust Badges */}
            <div className="mt-6 sm:mt-8 pt-6 sm:pt-8 border-t-2 border-gray-200">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
                <div className="flex flex-col items-center gap-2 p-3 sm:p-4 bg-gray-50 rounded-xl">
                  <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-rose-500 rounded-full flex items-center justify-center">
                    <i className="ri-lock-line text-white text-xl sm:text-2xl"></i>
                  </div>
                  <span className="font-bold text-gray-900 text-sm sm:text-base">Pago seguro</span>
                </div>
                <div className="flex flex-col items-center gap-2 p-3 sm:p-4 bg-gray-50 rounded-xl">
                  <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-rose-500 rounded-full flex items-center justify-center">
                    <i className="ri-download-cloud-line text-white text-xl sm:text-2xl"></i>
                  </div>
                  <span className="font-bold text-gray-900 text-sm sm:text-base">Descarga inmediata</span>
                </div>
                <div className="flex flex-col items-center gap-2 p-3 sm:p-4 bg-gray-50 rounded-xl">
                  <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-rose-500 rounded-full flex items-center justify-center">
                    <i className="ri-shield-check-line text-white text-xl sm:text-2xl"></i>
                  </div>
                  <span className="font-bold text-gray-900 text-sm sm:text-base">Garantía 100%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
